import { Grid2 as Grid, Menu, MenuItem, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { hasAnyAccessToResource } from "api/general";
import { useReward } from "api/rewards";
import { useSegments } from "api/segments";
import { useTiers } from "api/tiers";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { TranslationTabs } from "components/TranslationTabs/TranslationTabs";
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import { Translation } from "reduxState/translations/translationsSlice";
import { RewardFields, RewardFieldsBE, RewardType } from "types/rewards";
import { useStyles } from "../RewardForm.styles";
import { NameDescription } from "./NameDescription";

type Props = {
  isEditMode?: boolean;
  activeReward?: RewardType;
  rewardData?: RewardFieldsBE;
  availableLanguages: Translation[];
  activeTranslation: number;
  setActiveTranslation: (index: number) => void;
};

export function BasicSettings({
  isEditMode,
  activeReward,
  rewardData,
  availableLanguages,
  activeTranslation,
  setActiveTranslation,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const contextMenu = useContextMenu();
  const { values, setFieldValue } = useFormikContext<
    RewardFields & { areAutoGeneratedCouponCodes?: string }
  >();
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const { useInfiniteSegmentsList } = useSegments();
  const { useInfiniteTiersList } = useTiers();
  const { useInfiniteRewardCategoriesList } = useReward();

  const [searchSegements, setSearchSegments] = useState("");
  const [searchTiers, setSearchTiers] = useState("");
  const [searchCategory, setSearchCategory] = useState("");

  const {
    data: infiniteSegmentsList,
    size: segmentsSize,
    setSize: setSegmentsSize,
    isValidating: isSegmentsListValidating,
  } = useInfiniteSegmentsList({
    filters: searchSegements
      ? [
          {
            condition: "like",
            query: "name",
            value: searchSegements,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: segmentsList, anyElementsToFetch: anySegmentsToFetch } =
    useManageInfiniteScroll(infiniteSegmentsList || []);

  const {
    data: infiniteTiersList,
    size: tiersSize,
    setSize: setTiersSize,
    isValidating: isTiersListValidating,
  } = useInfiniteTiersList({
    filters: searchTiers
      ? [
          {
            condition: "like",
            query: "name",
            value: searchTiers,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: tiersList, anyElementsToFetch: anyTiersToFetch } =
    useManageInfiniteScroll(infiniteTiersList || []);

  const {
    data: infiniteRewardCategories,
    size: rewardCategorySize,
    setSize: setRewardCategorySize,
    isValidating,
  } = useInfiniteRewardCategoriesList({
    filters: searchCategory
      ? [
          {
            condition: "like",
            query: "name",
            value: searchCategory,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const {
    infiniteData: rewardCategories,
    anyElementsToFetch: anyRewardCategoriesToFetch,
  } = useManageInfiniteScroll(infiniteRewardCategories || []);

  const isAutoGeneratedCoupon = values.areAutoGeneratedCouponCodes === "true";
  const shouldDisplayRedemptionLimit =
    activeReward === "material" ||
    (activeReward === "coupon" && isAutoGeneratedCoupon);

  return (
    <OLPaper className={classes.OLPaper}>
      <FieldArray
        name="translations"
        render={(translationsArray: FieldArrayRenderProps) => (
          <Grid container spacing={8}>
            <NameDescription
              index={0}
              translation={values.translations[0]}
              isEditMode={isEditMode}
              setFieldValue={setFieldValue}
            />
            {values?.translations?.length === 1 &&
            availableLanguages?.length ? (
              <>
                <OLButton
                  className={classes.addTranslation}
                  color="secondary"
                  onClick={
                    availableLanguages.filter(
                      (lang: any) =>
                        !values?.translations?.find(
                          (openLang) =>
                            (openLang?.code || openLang?.locale) === lang?.code,
                        ),
                    ).length
                      ? contextMenu.handleMenuOpen
                      : undefined
                  }
                  startIcon={<PlusIcon />}
                  data-test-id="reward-form-basic-settings-add-translations-button"
                >
                  {message({ id: "addTranslation" })}
                </OLButton>
                <Menu
                  anchorEl={contextMenu.anchor}
                  anchorOrigin={{
                    horizontal: "right",
                    vertical: "top",
                  }}
                  onClose={contextMenu.handleMenuClose}
                  open={contextMenu.isMenuOpen}
                  transformOrigin={{
                    horizontal: "right",
                    vertical: "top",
                  }}
                >
                  {availableLanguages
                    .filter(
                      (lang) =>
                        !values?.translations?.find(
                          (openLang) =>
                            (openLang?.code || openLang?.locale) === lang?.code,
                        ),
                    )
                    .map((language) => (
                      <MenuItem
                        key={language.code}
                        onClick={() => {
                          contextMenu.handleMenuClose();
                          translationsArray.push({
                            code: language.code,
                            languageName: language.name,
                          });
                        }}
                        data-test-id={`reward-form-${language.code}-translation-menu-item`}
                      >
                        {language.name}
                      </MenuItem>
                    ))}
                </Menu>
              </>
            ) : null}
            {values.translations.length > 1 ? (
              <>
                <Grid size={12}>
                  <Typography className={classes.sectionHeader}>
                    {message({ id: "translations" })}
                  </Typography>
                </Grid>
                <TranslationTabs
                  activeTranslation={activeTranslation}
                  setActiveTranslation={setActiveTranslation}
                  translations={values.translations}
                  availableLanguages={availableLanguages}
                  push={translationsArray.push}
                  setFieldValue={setFieldValue}
                  isEditMode={isEditMode}
                />
                <NameDescription
                  index={activeTranslation}
                  translation={values.translations[activeTranslation]}
                  isEditMode={isEditMode}
                  setFieldValue={setFieldValue}
                />
              </>
            ) : null}
          </Grid>
        )}
      />
      <SelectInput
        className={classes.fullWidth}
        items={[
          {
            id: "true",
            label: message({ id: "rewardIsAlwaysAvailable" }),
          },
          {
            id: "false",
            label: message({
              id: "rewardIsAvailableInSpecificPeriodOfTime",
            }),
          },
        ]}
        label={
          <RequiredFieldLabel
            text={message({
              id: "rewardAvailability",
            })}
          />
        }
        name="activity.allTime"
        data-test-id="reward-form-reward-availability-select-input"
      />
      {values.activity?.allTime === "false" && (
        <Grid container spacing={12}>
          <Grid size={6}>
            <DateTimeInput
              width="100%"
              format="yyyy-MM-dd HH:mmXXX"
              label={
                <RequiredFieldLabel text={message({ id: "availableFrom" })} />
              }
              name="activity.from"
              data-test-id="reward-form-reward-available-from-input"
            />
          </Grid>
          <Grid size={6}>
            <DateTimeInput
              width="100%"
              format="yyyy-MM-dd HH:mmXXX"
              label={
                <RequiredFieldLabel text={message({ id: "availableTo" })} />
              }
              name="activity.to"
              data-test-id="reward-form-reward-available-to-input"
            />
          </Grid>
        </Grid>
      )}
      {values.reward !== "dynamic_coupon" && (
        <SelectInput
          className={classes.fullWidth}
          items={[
            {
              id: "true",
              label: message({ id: "rewardIsAlwaysVisible" }),
            },
            {
              id: "false",
              label: message({
                id: "rewardIsVisibleInSpecificPeriodOfTime",
              }),
            },
          ]}
          label={
            <RequiredFieldLabel
              text={message({
                id: "rewardVisibilityInCatalog",
              })}
            />
          }
          name="visibility.allTime"
          data-test-id="reward-form-reward-visibility-in-catalog-select-input"
        />
      )}
      {values.visibility?.allTime === "false" && (
        <Grid container spacing={12}>
          <Grid size={6}>
            <DateTimeInput
              width="100%"
              format="yyyy-MM-dd HH:mmXXX"
              label={
                <RequiredFieldLabel text={message({ id: "visibleFrom" })} />
              }
              name="visibility.from"
              data-test-id="reward-form-reward-visible-from-input"
            />
          </Grid>
          <Grid size={6}>
            <DateTimeInput
              width="100%"
              format="yyyy-MM-dd HH:mmXXX"
              label={<RequiredFieldLabel text={message({ id: "visibleTo" })} />}
              name="visibility.to"
              data-test-id="reward-form-reward-visible-to-input"
            />
          </Grid>
        </Grid>
      )}
      <SelectInput
        className={classes.fullWidth}
        items={[
          {
            id: "all",
            label: message({ id: "availableForEveryone" }),
          },
          {
            id: "segment",
            label: message({
              id: "availableForSpecificSegment",
            }),
          },
          {
            id: "level",
            label: message({
              id: "availableForSpecificTier",
            }),
          },
        ]}
        label={message({ id: "rewardTargeting" })}
        name="target"
        data-test-id="reward-form-reward-targeting-select-input"
      />
      {values.target === "segment" && (
        <>
          <SelectInput
            name="segments"
            multiple
            displaySearch
            items={segmentsList.map((segment) => ({
              label: segment.name,
              id: segment.segmentId,
            }))}
            label={
              <RequiredFieldLabel text={message({ id: "targetedSegments" })} />
            }
            placeholder={message({
              id: "targetedSegmentsPlaceholder",
            })}
            data-test-id="reward-form-targeted-segments-multi-input"
            asyncSearch={(value) => {
              setSearchSegments(value);
            }}
            initialValue={
              isEditMode
                ? rewardData?.segments
                  ? rewardData.segments.map((segmentId) => ({
                      id: segmentId,
                      label: rewardData.segmentNames[segmentId],
                    }))
                  : []
                : []
            }
            anyElementsToFetch={anySegmentsToFetch}
            loadMoreElements={() =>
              !isSegmentsListValidating && setSegmentsSize(segmentsSize + 1)
            }
          />
        </>
      )}
      {values.target === "level" && (
        <>
          <SelectInput
            name="levels"
            multiple
            displaySearch
            items={tiersList.map((level) => ({
              label: level.name,
              id: level.levelId,
              disabled: !hasAnyTierPermission,
            }))}
            label={
              <RequiredFieldLabel text={message({ id: "targetedTiers" })} />
            }
            placeholder={message({
              id: "targetedTiersPlaceholder",
            })}
            data-test-id="reward-form-targeted-tiers-multi-input"
            asyncSearch={(value) => {
              setSearchTiers(value);
            }}
            initialValue={
              isEditMode
                ? rewardData?.levels
                  ? rewardData.levels.map((levelId) => ({
                      id: levelId,
                      label: rewardData.levelNames[levelId],
                    }))
                  : []
                : []
            }
            loadMoreElements={() =>
              !isTiersListValidating && setTiersSize(tiersSize + 1)
            }
            anyElementsToFetch={anyTiersToFetch}
          />
        </>
      )}
      {shouldDisplayRedemptionLimit && (
        <Grid container direction="column">
          <Grid size={12}>
            <NumberFormatInput
              name="usageLimit.general"
              label={message({ id: "redemptionLimit" })}
              data-test-id="reward-form-redemption-limit-input"
            />
          </Grid>
          <Grid size={12}>
            <Typography className={classes.inputInfo}>
              {message({
                id: "redemptionLimitInfo",
              })}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container direction="column">
        <Grid size={12}>
          <NumberFormatInput
            name="usageLimit.perUser"
            label={message({ id: "redemptionLimitPerMember" })}
            data-test-id="reward-form-redemption-limit-per-member-input"
          />
        </Grid>
        <Grid size={12}>
          <Typography className={classes.inputInfo}>
            {message({
              id: "redemptionLimitPerMemberInfo",
            })}
          </Typography>
        </Grid>
      </Grid>
      <Grid size={12}>
        <SelectInput
          name="categories"
          multiple
          displaySearch
          items={rewardCategories?.map((item) => ({
            id: item.rewardCategoryId,
            label: item.name,
          }))}
          label={message({ id: "category" })}
          placeholder={message({
            id: "addCategory",
          })}
          data-test-id="reward-form-additional-settings-categories-multi-select"
          asyncSearch={(value) => {
            setSearchCategory(value);
          }}
          initialValue={
            isEditMode
              ? rewardData?.categories
                ? rewardData.categories.map((categoryId) => ({
                    id: categoryId,
                    label: rewardData.categoryNames[categoryId],
                  }))
                : []
              : []
          }
          anyElementsToFetch={anyRewardCategoriesToFetch}
          loadMoreElements={() =>
            !isValidating && setRewardCategorySize(rewardCategorySize + 1)
          }
        />
      </Grid>
    </OLPaper>
  );
}
